/**
 * Formats raw DQDL conformance error messages into human-readable text. Each formatter handles a
 * specific category of rule; unrecognized messages pass through unchanged.
 */

type MessageFormatter = (message: string, rule: string) => string | null

/**
 * A DQDL rule family whose evaluation result is a percentage of rows. To support a new
 * percentage-style rule type, add an entry here - the rest of the formatting is data-driven.
 */
interface PercentageFamily {
  /**
   * DQDL rule-type prefix the rule text starts with, e.g. "IsComplete".
   */
  prefix: string
  /**
   * Past-tense verb describing a row that satisfied the rule, e.g. "populated".
   */
  verb: string
  /**
   * Whether the rule implies a 100% requirement when it carries no explicit threshold.
   */
  impliesFullThreshold: boolean
}

const PERCENTAGE_FAMILIES: PercentageFamily[] = [
  { prefix: "Completeness", verb: "passed", impliesFullThreshold: false },
  { prefix: "ColumnValues", verb: "matched", impliesFullThreshold: true },
  { prefix: "IsComplete", verb: "populated", impliesFullThreshold: true },
  { prefix: "IsPrimaryKey", verb: "unique", impliesFullThreshold: true },
]

/**
 * Returns the percentage-rule family a rule belongs to, or null if it isn't one.
 */
function percentageFamily(rule: string): PercentageFamily | null {
  return PERCENTAGE_FAMILIES.find((family) => rule.startsWith(family.prefix)) ?? null
}

/**
 * Converts a decimal ratio (0-1) to a display percentage string, removing trailing zeros.
 */
function toPercent(decimal: number): string {
  const pct = Math.round(decimal * 1000) / 10
  return pct % 1 === 0 ? String(Math.round(pct)) : String(pct)
}

/**
 * Extracts the actual value from a Glue failure message like "Value: 0.9 does not meet..."
 */
function extractValue(message: string): number | null {
  const match = message.match(/^Value:\s*([\d.]+)\s+does not meet the constraint requirement/)
  if (!match) return null
  const num = parseFloat(match[1])
  if (isNaN(num) || num > 1) return null
  return num
}

/**
 * Returns the rule text with any trailing `where "<predicate>"` clause removed. The predicate is a
 * SparkSQL expression that can itself contain numeric comparisons (e.g. `where "AGE >= 18"`);
 * stripping it keeps those from being mistaken for the rule's own threshold.
 */
function ruleConstraint(rule: string): string {
  return rule.split(/\s+where\s+/i)[0]
}

/**
 * Extracts the required threshold from the rule's constraint. Handles: `Completeness "col" >=
 * 0.95`, `with threshold >= 0.8`, `with threshold > 0.9`.
 */
function extractThreshold(rule: string): { value: number; operator: string } | null {
  const constraint = ruleConstraint(rule)

  const withThreshold = constraint.match(/with\s+threshold\s*(>=|>|<=|<|=)\s*([\d.]+)/)
  if (withThreshold) {
    return { operator: withThreshold[1], value: parseFloat(withThreshold[2]) }
  }

  const directThreshold = constraint.match(/(>=|>|<=|<|=)\s*([\d.]+)\s*$/)
  if (directThreshold) {
    return { operator: directThreshold[1], value: parseFloat(directThreshold[2]) }
  }

  return null
}

/**
 * Formats the "required" portion, including the operator when it's not the common `>=`.
 */
function formatRequired(threshold: { value: number; operator: string }): string {
  const pct = toPercent(threshold.value)
  if (threshold.operator === ">") return `>${pct}% required`
  if (threshold.operator === "<=") return `<=${pct}% required`
  if (threshold.operator === "<") return `<${pct}% required`
  return `${pct}% required`
}

const formatPercentageRule: MessageFormatter = (message, rule) => {
  const family = percentageFamily(rule)
  if (!family) return null

  const actual = extractValue(message)
  if (actual === null) return null
  const actualPct = toPercent(actual)

  const threshold = extractThreshold(rule)
  if (threshold) {
    return `${actualPct}% of rows ${family.verb} (${formatRequired(threshold)})`
  }
  if (family.impliesFullThreshold) {
    return `${actualPct}% of rows ${family.verb} (100% required)`
  }
  return `${actualPct}% of rows ${family.verb}`
}

/**
 * Formats Glue's alternate `<pct> % of rows passed the threshold` message (emitted by rules such as
 * ColumnDataType and DataFreshness) into the same `X% of rows passed (Y% required)` shape as the
 * Value-ratio rules. The percentage is already in the message; the required threshold comes from
 * the rule, defaulting to 100%.
 */
const formatThresholdPassRule: MessageFormatter = (message, rule) => {
  const match = message.match(/^([\d.]+)\s*%\s*of rows passed the threshold/)
  if (!match) return null

  const actualPct = toPercent(parseFloat(match[1]) / 100)
  const threshold = extractThreshold(rule)
  if (threshold) {
    return `${actualPct}% of rows passed (${formatRequired(threshold)})`
  }
  return `${actualPct}% of rows passed (100% required)`
}

const formatters: MessageFormatter[] = [formatPercentageRule, formatThresholdPassRule]

/**
 * Formats a conformance error message for display. Applies registered formatters in order; the
 * first match wins. Falls back to the raw message if no formatter matches.
 */
export function formatConformanceMessage(message: string, rule: string): string {
  for (const formatter of formatters) {
    const result = formatter(message, rule)
    if (result !== null) return result
  }
  return message
}
